'use client';

import { useState } from 'react';

import {
  getNearCitySchedulesPage,
  hydrateNearCityGroups,
  NearCitySchedules,
  NEAR_CITY_PAGE_SIZE,
} from '@/app/(main)/(pages)/busca/[slug]/_services/get-near-city-schedules.service';
import { SearchApiScheduleGroup } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { useToast } from '@/components/ui/use-toast';
import { useLoading } from '@/hooks/use-loading.hook';
import { useUserLocation } from '@/hooks/use-user-location.hook';
import { getLogger } from '@/logger/logger';

const logger = getLogger('search.near-city-expansion');

interface UseNearCityExpansionParams {
  nearCitySchedules: NearCitySchedules | null;
  searchType: string;
  procedureCodes: string[];
}

interface UseNearCityExpansion {
  schedules: SearchPartnerData[];
  hasMore: boolean;
  isExpanded: boolean;
  isLoading: boolean;
  expand(): Promise<void>;
}

export function useNearCityExpansion({
  nearCitySchedules,
  searchType,
  procedureCodes,
}: UseNearCityExpansionParams): UseNearCityExpansion {
  const { userLocation } = useUserLocation();
  const { toast } = useToast();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [schedules, setSchedules] = useState<SearchPartnerData[]>(nearCitySchedules?.schedules ?? []);
  const [pendingGroups, setPendingGroups] = useState<SearchApiScheduleGroup[]>(
    nearCitySchedules?.pendingGroups ?? [],
  );
  const [hasMorePages, setHasMorePages] = useState<boolean>(nearCitySchedules?.hasMore ?? false);
  const [page, setPage] = useState<number>(0);

  const expandFromPendingGroups = async (nextPage: number): Promise<void> => {
    const groups = pendingGroups.slice(0, nextPage * NEAR_CITY_PAGE_SIZE - schedules.length);
    const hydrated = await hydrateNearCityGroups(groups, searchType);

    setSchedules((current) => [...current, ...hydrated]);
    setPendingGroups((current) => current.slice(groups.length));
    setPage(nextPage);
  };

  const expandFromApi = async (nextPage: number, distance: number): Promise<void> => {
    const { schedules: pageSchedules, hasMore } = await getNearCitySchedulesPage({
      procedureCodes,
      searchType,
      city: userLocation?.city,
      state: userLocation?.state,
      latitude: userLocation?.latitude,
      longitude: userLocation?.longitude,
      distance,
      page: nextPage,
    });

    if (!pageSchedules.length) {
      setHasMorePages(false);

      return;
    }

    setSchedules((current) => [...current, ...pageSchedules]);
    setHasMorePages(hasMore);
    setPage(nextPage);
  };

  const expand = async (): Promise<void> => {
    if (!nearCitySchedules) {
      return;
    }

    const nextPage = page + 1;

    try {
      startLoading();

      if (pendingGroups.length) {
        await expandFromPendingGroups(nextPage);

        return;
      }

      await expandFromApi(nextPage, nearCitySchedules.distance);
    } catch (error) {
      logger.error('Failed to expand the near-city suggestions', {
        search: { event: 'near_city_expansion_failed', procedure_codes: procedureCodes },
        error,
      });
      toast.error('Erro ao carregar mais resultados.');
    } finally {
      stopLoading();
    }
  };

  return {
    schedules,
    hasMore: hasMorePages || pendingGroups.length > 0,
    isExpanded: page > 0,
    isLoading,
    expand,
  };
}
